import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Button,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  Divider,
  Chip
} from '@mui/material';
import { CloseOutlined, SearchOutlined, UserAddOutlined } from '@ant-design/icons';
import axios from 'api/axiosInstance';
import endpoints from '../api/endpoints';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { formatDoctorName } from '../utils/doctorName';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  maxHeight: '90vh',
  overflow: 'auto'
};

export default function AdminBookAppointmentModal({ open, onClose, onSuccess, offlineOnly = false }) {
  const companyId = localStorage.getItem('tenant_id');

  // Two modes:
  //   'lookup'  — admin is searching for an existing patient (initial)
  //   'new'     — admin is creating a new patient inline
  //   'picked'  — admin selected an existing patient; form is prefilled/locked
  const [mode, setMode] = useState('lookup');
  const [selectedPatient, setSelectedPatient] = useState(null); // existing patient object
  const [patientQuery, setPatientQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  // Form fields (used when mode = 'new'; locked read-only when mode = 'picked')
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [hospitalId, setHospitalId] = useState('');

  // Doctor selection cascade
  const [specializations, setSpecializations] = useState([]);
  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [availabilityData, setAvailabilityData] = useState({});
  const [walkInByDate, setWalkInByDate] = useState({});
  const [availableDates, setAvailableDates] = useState([]);
  const [date, setDate] = useState('');
  const [doctorSlots, setDoctorSlots] = useState([]);
  const [time, setTime] = useState('');

  // Loading states
  const [loadingSpecs, setLoadingSpecs] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Feedback
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMode('lookup');
      setSelectedPatient(null);
      setPatientQuery('');
      setSuggestions([]);
      setName('');
      setEmail('');
      setPhone('');
      setHospitalId('');
      setSelectedSpecialization('');
      setDoctors([]);
      setSelectedDoctor('');
      setAvailabilityData({});
      setWalkInByDate({});
      setAvailableDates([]);
      setDate('');
      setDoctorSlots([]);
      setTime('');
      setErrors({});
      setSubmitError('');
    }
  }, [open]);

  // Debounced patient search as admin types
  useEffect(() => {
    if (mode !== 'lookup') return;
    if (!patientQuery || patientQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        setSearching(true);
        const token = localStorage.getItem('token');
        const url = `${endpoints.endUsers.list(companyId)}?search=${encodeURIComponent(patientQuery.trim())}`;
        const resp = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const results = resp.data?.results || [];
        setSuggestions(results);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [patientQuery, mode, companyId]);

  const pickPatient = (patient) => {
    setSelectedPatient(patient);
    setName(`${patient.first_name || ''} ${patient.last_name || ''}`.trim());
    setEmail(patient.email || '');
    setPhone(patient.mobile || '');
    setHospitalId(patient.hospital_id || '');
    setMode('picked');
    setErrors({});
  };

  const startNewPatient = () => {
    setSelectedPatient(null);
    setName('');
    setEmail('');
    setPhone('');
    setHospitalId('');
    setMode('new');
    setErrors({});
  };

  const backToLookup = () => {
    setSelectedPatient(null);
    setMode('lookup');
  };

  // Fetch specializations on open
  useEffect(() => {
    if (open && companyId) {
      fetchSpecializations();
    }
  }, [open]);

  // Fetch doctors when specialization changes
  useEffect(() => {
    if (selectedSpecialization) {
      fetchDoctors(selectedSpecialization);
      setSelectedDoctor('');
      setAvailabilityData({});
      setAvailableDates([]);
      setDate('');
      setDoctorSlots([]);
      setTime('');
    } else {
      setDoctors([]);
    }
  }, [selectedSpecialization]);

  // Fetch slots when doctor changes
  useEffect(() => {
    if (selectedDoctor) {
      fetchDoctorSlots(selectedDoctor);
      setDate('');
      setDoctorSlots([]);
      setTime('');
    } else {
      setAvailabilityData({});
      setAvailableDates([]);
    }
  }, [selectedDoctor]);

  // Update time slots when date changes
  useEffect(() => {
    if (date && availabilityData[date]) {
      const slots = availabilityData[date].map((slot) => {
        const timeValue = slot.start.slice(0, 5);
        const [hours, minutes] = timeValue.split(':').map(Number);
        const hour12 = hours % 12 || 12;
        const ampm = hours < 12 ? 'AM' : 'PM';
        return { value: timeValue, label: `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}` };
      });
      setDoctorSlots(slots);
      setTime('');
    } else {
      setDoctorSlots([]);
    }
  }, [date, availabilityData]);

  const fetchSpecializations = async () => {
    try {
      setLoadingSpecs(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(endpoints.company.specializations(companyId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = Array.isArray(response.data) ? response.data : response.data.results || response.data.data || [];
      setSpecializations(data);
    } catch (err) {
      console.error('Error fetching specializations:', err);
    } finally {
      setLoadingSpecs(false);
    }
  };

  const fetchDoctors = async (specializationId) => {
    try {
      setLoadingDoctors(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(endpoints.company.getCompanyDoctor(companyId, specializationId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoctors(response?.data?.doctors || []);
    } catch (err) {
      console.error('Error fetching doctors:', err);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchDoctorSlots = async (doctorId) => {
    try {
      setLoadingSlots(true);
      const response = await axios.get(endpoints.company.doctorSlots(doctorId));
      const data = response.data?.data || response.data;
      const walkIns = response.data?.walk_in_by_date || {};
      setAvailabilityData(data);
      setWalkInByDate(walkIns);
      // A date is selectable if it has online slots OR accepts walk-ins.
      const dates = Array.from(
        new Set([...Object.keys(data).filter((k) => k !== 'range'), ...Object.keys(walkIns)])
      ).sort();
      setAvailableDates(dates);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setAvailabilityData({});
      setWalkInByDate({});
      setAvailableDates([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const validate = () => {
    const e = {};
    if (mode === 'lookup') {
      e.patient = 'Select an existing patient or create a new one';
    } else {
      if (!name.trim()) e.name = 'Patient name is required';
      if (mode === 'new' && email.trim()) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Invalid email';
      }
    }
    if (!selectedDoctor) e.doctor = 'Select a doctor';
    if (!date) e.date = 'Select a date';
    // Slot is optional: no slot → offline (walk-in) booking. But the day must
    // accept walk-ins, otherwise there is nothing to book.
    if (!time) {
      const w = walkInByDate[date];
      if (!w || !w.accept_walk_ins) {
        e.time = 'Pick a slot, or enable walk-ins for this day to book offline';
      } else if (w.is_full) {
        e.time = 'Walk-in capacity is full for this day';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('token');
      // For a picked existing patient, send hospital_id so the backend
      // lookup resolves deterministically to that exact user. For "new
      // patient" mode, force_new_user=true skips the phone-dedupe so a
      // shared family phone doesn't attach the appointment to a sibling.
      // Slot chosen → online; no slot → offline (walk-in, token assigned by API).
      const isOffline = !time;
      const payload = {
        company_id: companyId,
        patient_name: name,
        user_email: email || undefined,
        user_mobile: phone || undefined,
        hospital_id: hospitalId || undefined,
        doctor_id: selectedDoctor,
        date_time: isOffline ? `${date}T00:00:00` : `${date}T${time}:00`,
        booking_type: isOffline ? 'OFFLINE' : 'ONLINE',
        force_new_user: mode === 'new'
      };

      await axios.post(endpoints.appointment.bookAppointment, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      const msg = typeof data === 'object' ? Object.values(data).flat().join('. ') : 'Failed to book appointment';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            {offlineOnly ? 'Walk-in (Offline) Appointment' : 'Book Appointment'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Form */}
        <Box sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* ── Patient selection ── */}
            {mode === 'lookup' && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                  Find Patient
                </Typography>
                <Autocomplete
                  freeSolo
                  options={suggestions}
                  loading={searching}
                  filterOptions={(x) => x}
                  inputValue={patientQuery}
                  onInputChange={(_, v) => setPatientQuery(v)}
                  onChange={(_, v) => {
                    if (v && typeof v === 'object') pickPatient(v);
                  }}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : `${opt.first_name || ''} ${opt.last_name || ''}`.trim()
                  }
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt.id} sx={{ alignItems: 'flex-start !important', py: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                            {`${opt.first_name || ''} ${opt.last_name || ''}`.trim() || '(no name)'}
                          </Typography>
                          {opt.hospital_id && (
                            <Chip size="small" label={opt.hospital_id} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#F0FDFA', color: '#0D9488' }} />
                          )}
                          {typeof opt.booking_count === 'number' && (
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              · {opt.booking_count} booking{opt.booking_count === 1 ? '' : 's'}
                            </Typography>
                          )}
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                          {[opt.mobile, opt.email].filter(Boolean).join(' · ') || 'No contact info'}
                        </Typography>
                        {opt.related_users && opt.related_users.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.25 }}>
                            Shares phone with: {opt.related_users.map(r => `${r.first_name || ''} ${r.last_name || ''}`.trim() || '?').join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search by Patient ID, name, phone, or email…"
                      error={!!errors.patient}
                      helperText={errors.patient || 'Type at least 2 characters'}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchOutlined style={{ fontSize: 16, color: '#94A3B8', marginRight: 6 }} />,
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Button
                  onClick={startNewPatient}
                  startIcon={<UserAddOutlined />}
                  sx={{ mt: 1, textTransform: 'none', color: '#0D9488' }}
                >
                  New patient — enter details manually
                </Button>
              </Box>
            )}

            {mode === 'picked' && selectedPatient && (
              <Box sx={{ border: '1px solid #CCFBF1', bgcolor: '#F0FDFA', borderRadius: 2, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F766E' }}>
                        {name}
                      </Typography>
                      {hospitalId && (
                        <Chip size="small" label={hospitalId} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#fff', color: '#0D9488', border: '1px solid #CCFBF1' }} />
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ color: '#134E4A', display: 'block' }}>
                      {[phone, email].filter(Boolean).join(' · ') || 'No contact info on file'}
                    </Typography>
                    {selectedPatient.related_users && selectedPatient.related_users.length > 0 && (
                      <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>
                        Shares phone with: {selectedPatient.related_users.map(r => `${r.first_name || ''} ${r.last_name || ''}`.trim() || '?').join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Button size="small" onClick={backToLookup} sx={{ textTransform: 'none', color: '#0D9488', ml: 1 }}>
                    Change
                  </Button>
                </Stack>
              </Box>
            )}

            {mode === 'new' && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    New Patient
                  </Typography>
                  <Button size="small" onClick={backToLookup} sx={{ textTransform: 'none', color: '#0D9488' }}>
                    Search existing instead
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="Patient Name" value={name} onChange={(e) => setName(e.target.value)}
                    error={!!errors.name} helperText={errors.name} fullWidth required
                  />
                  <TextField
                    label="Email (Optional)" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    error={!!errors.email} helperText={errors.email || 'Confirmation sent if provided'} fullWidth
                  />
                  <Box sx={{ '& .react-tel-input .form-control': { width: '100%' } }}>
                    <PhoneInput
                      country="in" value={phone} onChange={(val) => setPhone(val)}
                      preferredCountries={['in', 'us', 'gb', 'ae']} enableSearch
                      inputStyle={{ width: '100%' }}
                    />
                  </Box>
                  <TextField
                    label="Patient ID" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}
                    fullWidth placeholder="Leave blank to auto-generate"
                    helperText="Hospital registration number (optional)"
                  />
                </Stack>
              </Box>
            )}

            <Divider />
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Appointment
            </Typography>

            {/* Specialization — dropdown (kept as-is for long lists) */}
            <FormControl fullWidth>
              <InputLabel>Specialization</InputLabel>
              <Select
                value={selectedSpecialization}
                label="Specialization"
                onChange={(e) => setSelectedSpecialization(e.target.value)}
                disabled={loadingSpecs}
              >
                {loadingSpecs && (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                  </MenuItem>
                )}
                {specializations.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Doctor — pills */}
            {selectedSpecialization && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                  Select Doctor
                </Typography>
                {loadingDoctors ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CircularProgress size={16} /> <Typography variant="caption" sx={{ color: '#94A3B8' }}>Loading doctors…</Typography>
                  </Box>
                ) : doctors.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>No doctors with availability for this specialization</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {doctors.map((d) => {
                      const isSelected = selectedDoctor === d.id;
                      return (
                        <Chip
                          key={d.id}
                          label={formatDoctorName(d.name)}
                          clickable
                          onClick={() => setSelectedDoctor(d.id)}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            height: 36,
                            borderRadius: '18px',
                            px: 0.5,
                            bgcolor: isSelected ? '#0D9488' : '#F0FDFA',
                            color: isSelected ? '#fff' : '#0F766E',
                            border: `1.5px solid ${isSelected ? '#0D9488' : '#CCFBF1'}`,
                            '&:hover': { bgcolor: isSelected ? '#0F766E' : '#E6FAF8' },
                            transition: 'all 0.15s',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
                {errors.doctor && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.doctor}
                  </Typography>
                )}
              </Box>
            )}

            {/* Date — next 7 day pills + custom calendar */}
            {selectedDoctor && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                  Select Date
                </Typography>
                {loadingSlots ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <CircularProgress size={16} /> <Typography variant="caption" sx={{ color: '#94A3B8' }}>Loading availability…</Typography>
                  </Box>
                ) : availableDates.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>No available dates for this doctor</Typography>
                ) : (
                  <>
                    {/* Quick-pick: first 7 available dates as pills */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      {availableDates.slice(0, 7).map((d) => {
                        const dateObj = new Date(d + 'T00:00:00');
                        const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                        const dayNum = dateObj.getDate();
                        const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
                        const isSelected = date === d;
                        return (
                          <Chip
                            key={d}
                            clickable
                            onClick={() => setDate(d)}
                            label={
                              <Box sx={{ textAlign: 'center', lineHeight: 1.3 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: 'inherit' }}>
                                  {dayNum}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.85, color: 'inherit' }}>
                                  {dayName}, {monthName}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              height: 52,
                              minWidth: 62,
                              borderRadius: 2,
                              bgcolor: isSelected ? '#0D9488' : '#F8FAFC',
                              color: isSelected ? '#fff' : '#1E293B',
                              border: `1.5px solid ${isSelected ? '#0D9488' : '#E2E8F0'}`,
                              '&:hover': { bgcolor: isSelected ? '#0F766E' : '#F0FDFA' },
                              transition: 'all 0.15s',
                            }}
                          />
                        );
                      })}
                    </Box>
                    {/* Custom calendar input for dates beyond 7 days */}
                    <TextField
                      type="date"
                      size="small"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      inputProps={{
                        min: availableDates[0],
                        max: availableDates[availableDates.length - 1],
                      }}
                      helperText="Or pick a custom date"
                      sx={{ maxWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </>
                )}
                {errors.date && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.date}
                  </Typography>
                )}
              </Box>
            )}

            {/* Time — pills (optional: skipping it books an offline walk-in).
                Hidden entirely in offline-only mode (e.g. the walk-in QR page). */}
            {date && !offlineOnly && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                  Select Time <span style={{ textTransform: 'none', fontWeight: 400 }}>— optional, skip for a walk-in (offline) booking</span>
                </Typography>
                {time && (
                  <Button size="small" onClick={() => setTime('')} sx={{ textTransform: 'none', color: '#B45309', mb: 1, p: 0, minWidth: 0 }}>
                    Clear slot (book as offline / walk-in)
                  </Button>
                )}
                {doctorSlots.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>No online slots for this date — will be booked as a walk-in</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {doctorSlots.map((slot) => {
                      const isSelected = time === slot.value;
                      return (
                        <Chip
                          key={slot.value}
                          label={slot.label}
                          clickable
                          onClick={() => setTime(slot.value)}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            height: 34,
                            borderRadius: '17px',
                            bgcolor: isSelected ? '#0D9488' : '#F8FAFC',
                            color: isSelected ? '#fff' : '#334155',
                            border: `1.5px solid ${isSelected ? '#0D9488' : '#E2E8F0'}`,
                            '&:hover': { bgcolor: isSelected ? '#0F766E' : '#F0FDFA' },
                            transition: 'all 0.15s',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
                {errors.time && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.time}
                  </Typography>
                )}
              </Box>
            )}

            {/* Booking-type / token summary (online when a slot is picked,
                offline walk-in with a token when it is skipped). */}
            {date && (() => {
              const w = walkInByDate[date];
              if (time) {
                return (
                  <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
                    <strong>Online</strong> slot booking at {doctorSlots.find((s) => s.value === time)?.label || time}
                  </Alert>
                );
              }
              if (w && w.accept_walk_ins && !w.is_full) {
                return (
                  <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                    <strong>Offline</strong> (walk-in) — assigned token{' '}
                    <strong>T{w.next_token}</strong>
                    {w.remaining != null ? ` · ${w.remaining} walk-in slot${w.remaining === 1 ? '' : 's'} left` : ''}
                  </Alert>
                );
              }
              return (
                <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                  Pick a slot above — this day does not accept walk-ins{w?.is_full ? ' (walk-in capacity full)' : ''}.
                </Alert>
              );
            })()}

            {/* Error */}
            {submitError && <Alert severity="error">{submitError}</Alert>}

            {/* Actions */}
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button variant="outlined" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <CircularProgress size={20} color="inherit" /> : (date && !time ? 'Book Walk-in (Offline)' : 'Book Appointment')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}
